import { ButtonPrimeReact, CalendarPrimeReact } from "@finanedi.es/storybook"
import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { UpdateEmpresaCommandProps } from "api/Commands/UpdateEmpresaCommand"
import { UpdateDatosRegistroMercantilProps } from "api/Commands/UpdateEmpresaConfiguracionRegistroMercantil"
import FormikDropdown from "common/components/FormikFields/FormikDropdown"
import FormikTextArea from "common/components/FormikFields/FormikTextArea"
import { EN_CALENDAR } from "common/utils/date"
import { withFormik } from "formik"
import { TFunction } from "i18next"
import { addLocale } from "primereact/api"
import { DropdownChangeParams } from "primereact/dropdown"
import { ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import * as Yup from "yup"
import { ActiveCompanyUserHookProps } from "../../../../api/Usuario.hook"
import useEmpresa from "../../hooks/useEmpresa"
import { ActividadEmpresaFormikProps } from "./constants"
import { Verifactu } from "./Verifactu"

const ActividadEmpresa = ({
  setFieldValue,
  initialValues,
  isSubmitting,
  values,
  handleSubmit,
}: ActividadEmpresaFormikProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  addLocale("en", EN_CALENDAR)

  const {
    optionsCnae,
    optionsIndustria,
    optionsProvincias,
    getCnaeByIndustriaId,
  } = useEmpresa({ industriaId: initialValues.actividadPrincipal })

  const onChangeActividad = (e: DropdownChangeParams) => {
    setFieldValue("actividadPrincipal", e.value)
    setFieldValue("cnaeId", null)
    getCnaeByIndustriaId(e.value)
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`${"tw-mt-5 tw-pt-5 tw-border-neutral-25 tw-border-t"}`}
    >
      <Verifactu />
      <div className='tw-mt-5 tw-pt-5 tw-border-neutral-25 tw-border-t tw-w-full tw-text-left'>
        <p className='tw-text-lg tw-font-medium tw-mb-3 tw-text-primary'>
          {t("perfilEmpresa.company.actividad.title")}
        </p>
        <div className='tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2'>
          <div className='tw-grid-cols-1 md:tw-grid-cols-2'>
            <label>{t("perfilEmpresa.company.actividad.labelProvince")}</label>
            <div>
              <FormikDropdown
                id='provinciaRegistroMercantilId'
                placeholder={`${t(
                  "perfilEmpresa.company.actividad.placeholderProvince"
                )}`}
                options={optionsProvincias}
                name='provinciaRegistroMercantilId'
                value={values.provinciaRegistroMercantilId}
                className='tw-w-full'
                onChange={(ev: DropdownChangeParams) => {
                  setFieldValue("provinciaRegistroMercantilId", ev.value)
                }}
              />
            </div>
          </div>
          <div className='tw-grid-cols-1 md:tw-grid-cols-2'>
            <label>{t("perfilEmpresa.company.actividad.labelActivity")}</label>
            <div>
              <FormikDropdown
                id='actividadPrincipal'
                placeholder={`${t(
                  "perfilEmpresa.company.actividad.placeholderActivity"
                )}`}
                options={optionsIndustria}
                name='actividadPrincipal'
                value={values.actividadPrincipal}
                className='tw-w-full'
                onChange={onChangeActividad}
                emptyMessage={`${t(
                  "perfilEmpresa.company.actividad.errorCNAE"
                )}`}
              />
            </div>
          </div>
          <div className='tw-grid-cols-1 md:tw-grid-cols-2'>
            <label>{t("perfilEmpresa.company.actividad.labelCNAE")}</label>
            <div>
              <FormikDropdown
                id='cnaeId'
                placeholder={`${t(
                  "perfilEmpresa.company.actividad.placeholderCNAE"
                )}`}
                options={optionsCnae}
                name='cnaeId'
                value={values.cnaeId}
                className='tw-w-full'
                onChange={(ev: DropdownChangeParams) => {
                  setFieldValue("cnaeId", ev.value)
                }}
              />
            </div>
          </div>
          <div className='tw-grid-cols-1 md:tw-grid-cols-2'>
            <label>{t("perfilEmpresa.company.actividad.labelDate")}</label>
            <div>
              <CalendarPrimeReact
                id='fechaConstitucion'
                name='fechaConstitucion'
                selectionMode='single'
                value={
                  values?.fechaConstitucion
                    ? new Date(values.fechaConstitucion)
                    : undefined
                }
                locale={language}
                onChange={(e) => {
                  setFieldValue("fechaConstitucion", e.value)
                }}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='w-full'
                showIcon
              />
            </div>
          </div>
        </div>
      </div>

      <div className='tw-w-full tw-mt-5 tw-pt-5 tw-text-left tw-border-neutral-25 tw-border-t'>
        <p className='tw-text-lg tw-font-medium tw-mb-3 tw-text-primary'>
          {t("perfilEmpresa.company.actividad.registroMencantil")}
        </p>
        <label>{t("perfilEmpresa.company.actividad.labelMercantil")}</label>
        <FormikTextArea
          id='datosRegistroMercantil'
          name='datosRegistroMercantil'
          placeholder={`${t(
            "perfilEmpresa.company.actividad.placeholderMercantil"
          )}`}
          className='tw-w-full'
          value={values.datosRegistroMercantil}
          onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("datosRegistroMercantil", ev.target.value)
          }}
        />
      </div>
      <div className='tw-flex tw-justify-end tw-mt-5'>
        <ButtonPrimeReact
          className='!tw-bg-primary'
          loading={isSubmitting}
          type='submit'
        >
          {t("perfilEmpresa.company.actividad.saveLabel")}
        </ButtonPrimeReact>
      </div>
    </form>
  )
}

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    provinciaRegistroMercantilId: Yup.number().nullable(),
    actividadPrincipal: Yup.number().nullable(),
    cnaeId: Yup.number()
      .nullable()
      .test(
        "compareVariables",
        `${t("perfilEmpresa.company.actividad.errorCNAE")}`,
        // @ts-expect-error TS(2345): Argument of type '(this: TestContext<AnyObject>, v...
        (value, { parent }) => {
          if (value) {
            return true
          }
          if (parent.actividadPrincipal) {
            return false
          }
        }
      ),
    datosRegistroMercantil: Yup.string().nullable(),
  })

const Actividad = withFormik({
  mapPropsToValues: ({
    activeCompany,
    registroMercantil,
  }: {
    activeCompany: ActiveCompanyUserHookProps
    registroMercantil?: string
    t: TFunction
    updateActiveCompany: UseMutateAsyncFunction<
      void,
      Error,
      UpdateEmpresaCommandProps,
      unknown
    >
    updateRegistroMercantil: (
      data: UpdateDatosRegistroMercantilProps
    ) => Promise<void>
    activeCompanyUpdateSuccess: () => void
  }) => {
    return {
      actividadPrincipal: activeCompany?.cnae?.industriaId,
      cnaeId: activeCompany?.cnae?.id,
      provinciaRegistroMercantilId: activeCompany?.provinciaId,
      fechaConstitucion: activeCompany?.fechaConstitucion,
      datosRegistroMercantil: registroMercantil || "",
    }
  },
  validationSchema: ({ t }: { t: TFunction }) => validationSchema(t),
  handleSubmit: async (values, { props, setSubmitting, resetForm }) => {
    const { cnae, provinciaId, fechaConstitucion } = props.activeCompany
    const {
      // ActividadPrincipal no es requerido en el backend, se destructura para no enviarlo.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      actividadPrincipal: _,
      datosRegistroMercantil,
      ...empresa
    } = values
    const registroMercantilChanged =
      datosRegistroMercantil !== props.registroMercantil
    const empresaChanged =
      (empresa.cnaeId && cnae) !== cnae?.id ||
      empresa.fechaConstitucion !== fechaConstitucion ||
      empresa.provinciaRegistroMercantilId !== provinciaId

    setSubmitting(true)
    try {
      empresaChanged &&
        (await props.updateActiveCompany({
          ...empresa,
          fechaConstitucion: empresa?.fechaConstitucion
            ? backendDateFormat(new Date(empresa.fechaConstitucion))
            : "",
          id: props.activeCompany.id,
        }))
      registroMercantilChanged &&
        (await props.updateRegistroMercantil({
          empresaId: props.activeCompany.id,
          datosRegistroMercantil,
        }))
      if (empresaChanged || registroMercantilChanged) {
        props.activeCompanyUpdateSuccess()
        resetForm({ values })
      }
      setSubmitting(true)
    } catch (e) {
      setSubmitting(false)
    }
  },
})(ActividadEmpresa)

export default Actividad
